// src/shared/auth/authEvents.ts

type AuthEventType = 'login' | 'logout';

type AuthEvent = {
  v: 1;
  type: AuthEventType;
  at: number;
  src: string;
};

const CHANNEL = 'pacmaster:auth:events';
const STORAGE_KEY = 'pacmaster:auth:event';
const LOCAL_EVENT = 'pacmaster:auth:event:local';

function tabId(): string {
  try {
    return (globalThis as any).crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
  } catch {
    return `${Date.now()}-${Math.random()}`;
  }
}

const TAB_ID = tabId();

function canUseLocalStorage(): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
  } catch {
    return false;
  }
}

function canUseBroadcastChannel(): boolean {
  try {
    return typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined';
  } catch {
    return false;
  }
}

export function postAuthEvent(type: AuthEventType) {
  const evt: AuthEvent = { v: 1, type, at: Date.now(), src: TAB_ID };

  // Same-tab listeners (e.g. mounted data streams behind inline login)
  // must hear this event immediately as well.
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent<AuthEvent>(LOCAL_EVENT, { detail: evt }));
    }
  } catch {
    // ignore
  }

  // Prefer BroadcastChannel (low latency, no storage writes)
  if (canUseBroadcastChannel()) {
    try {
      const bc = new BroadcastChannel(CHANNEL);
      bc.postMessage(evt);
      bc.close();
    } catch {
      // ignore
    }
  }

  // Fallback: localStorage event fan-out across tabs
  if (canUseLocalStorage()) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(evt));
    } catch {
      // ignore
    }
  }
}

export function subscribeAuthEvents(handler: (evt: AuthEvent) => void): () => void {
  let bc: BroadcastChannel | null = null;

  const onMsg = (data: any, opts?: { allowSelf?: boolean }) => {
    const evt = data as Partial<AuthEvent>;
    if (!evt || evt.v !== 1) return;
    if (!opts?.allowSelf && evt.src === TAB_ID) return;
    if (evt.type !== 'login' && evt.type !== 'logout') return;
    if (typeof evt.at !== 'number') return;
    handler(evt as AuthEvent);
  };

  if (canUseBroadcastChannel()) {
    try {
      bc = new BroadcastChannel(CHANNEL);
      bc.onmessage = (ev: MessageEvent) => onMsg(ev?.data);
    } catch {
      bc = null;
    }
  }

  const onStorage = (e: StorageEvent) => {
    if (e.key !== STORAGE_KEY) return;
    if (!e.newValue) return;
    try {
      onMsg(JSON.parse(e.newValue));
    } catch {
      // ignore
    }
  };

  const onLocal = (e: Event) => {
    try {
      const ce = e as CustomEvent<AuthEvent>;
      onMsg(ce.detail, { allowSelf: true });
    } catch {
      // ignore
    }
  };

  try {
    window.addEventListener('storage', onStorage);
    window.addEventListener(LOCAL_EVENT, onLocal as EventListener);
  } catch {
    // ignore
  }

  return () => {
    try {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(LOCAL_EVENT, onLocal as EventListener);
    } catch {
      // ignore
    }
    try {
      bc?.close();
    } catch {
      // ignore
    }
  };
}
